import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage, LANGUAGE_OPTIONS } from '../context/LanguageContext';
import CountrySelect from './CountrySelect';
import DatePicker from './DatePicker';
import TimeZoneSelect from './TimeZoneSelect';

const Signup = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    first_name_eng: '',
    last_name_eng: '',
    nickname: '',
    email: '',
    password: '',
    gender: '',
    birth_date: '',
    weight: '',
    ftp: '',
    country: '',
    language: 'ru',
    time_zone: '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [vpnDetected, setVpnDetected] = useState(false);
  const [vpnChecking, setVpnChecking] = useState(false);
  const [vpnInitialChecking, setVpnInitialChecking] = useState(true);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const fileInputRef = useRef(null);

  const VPN_ORG_KEYWORDS = ['vpn', 'nordvpn', 'expressvpn', 'protonvpn', 'mullvad', 'windscribe', 'surfshark', 'cyberghost', 'ipvanish', 'purevpn', 'hidemyass', 'torguard', 'proxy', 'anonymizer', 'tunnel'];

  const checkVPN = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 6000);
      const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
      clearTimeout(timer);

      if (!res.ok) return false;
      const data = await res.json();
      if (data.error) return false;

      // Timezone mismatch: VPN changes IP location but not browser timezone
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const ipTz = data.timezone;
      if (browserTz && ipTz && browserTz !== ipTz) return true;

      // Known VPN/proxy provider in ISP/org name
      const org = (data.org || '').toLowerCase();
      if (VPN_ORG_KEYWORDS.some(kw => org.includes(kw))) return true;

      return false;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    checkVPN().then(detected => {
      if (!cancelled) {
        setVpnDetected(detected);
        setVpnInitialChecking(false);
      }
    });
    return () => { cancelled = true; };
  }, [checkVPN]);

  useEffect(() => {
    if (!vpnDetected) return;
    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.overflowY = 'scroll';
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.overflowY = '';
      window.scrollTo(0, scrollY);
    };
  }, [vpnDetected]);


  const handleVpnRecheck = async () => {
    setVpnChecking(true);
    const detected = await checkVPN();
    setVpnDetected(detected);
    setVpnChecking(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Проверка типа файла
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('profile.invalidFileType'));
      return;
    }

    // Проверка размера файла (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('profile.fileTooLarge'));
      return;
    }

    setAvatarFile(file);
    setError('');

    // Создание preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Prepare data, converting empty strings to undefined
      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        nickname: formData.nickname,
        email: formData.email,
        password: formData.password,
        language: formData.language,
      };

      if (formData.gender) submitData.gender = formData.gender;
      if (formData.birth_date) submitData.birth_date = formData.birth_date;
      if (formData.weight) submitData.weight = parseFloat(formData.weight);
      if (formData.ftp) {
        submitData.ftp = parseInt(formData.ftp);
      } else if (formData.ftp === '') {
        submitData.ftp = null;
      }
      if (formData.country) submitData.country = formData.country;
      if (formData.time_zone) submitData.time_zone = formData.time_zone;
      if (formData.first_name_eng) submitData.first_name_eng = formData.first_name_eng;
      if (formData.last_name_eng) submitData.last_name_eng = formData.last_name_eng;

      // Регистрация пользователя
      const user = await signup(submitData);

      // Если есть файл аватара, загружаем его
      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);

        try {
          await axios.post('/api/upload/avatar', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (err) {
          console.error('Failed to upload avatar, but user was created:', err);
        }
      }

      navigate('/profile');
    } catch (err) {
      setError(err.response?.data?.error || t('auth.signupFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {vpnDetected && (
        <div className="vpn-modal-overlay">
          <div className="vpn-modal">
            <div className="vpn-warning-icon">🔒</div>
            <h3 className="vpn-warning-title">{t('auth.vpnTitle')}</h3>
            <p className="vpn-warning-desc">{t('auth.vpnDesc')}</p>
            <button
              className="btn btn-primary vpn-btn-check"
              onClick={handleVpnRecheck}
              disabled={vpnChecking}
            >
              {vpnChecking ? t('auth.vpnChecking') : t('auth.vpnDisabled')}
            </button>
          </div>
        </div>
      )}

      <h1 className="auth-title">{t('auth.joinLeague')}</h1>
      <p className="auth-subtitle">{t('auth.joinSubtitle')}</p>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name">{t('signup.firstName')} {t('signup.required')}</label>
            <input
              id="first_name"
              name="first_name"
              type="text"
              value={formData.first_name}
              onChange={handleChange}
              placeholder={t('signup.firstNamePlaceholder')}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_name">{t('signup.lastName')} {t('signup.required')}</label>
            <input
              id="last_name"
              name="last_name"
              type="text"
              value={formData.last_name}
              onChange={handleChange}
              placeholder={t('signup.lastNamePlaceholder')}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name_eng">{t('signup.firstNameEng')}</label>
            <input
              id="first_name_eng"
              name="first_name_eng"
              type="text"
              value={formData.first_name_eng}
              onChange={handleChange}
              placeholder={t('signup.firstNameEngPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="last_name_eng">{t('signup.lastNameEng')}</label>
            <input
              id="last_name_eng"
              name="last_name_eng"
              type="text"
              value={formData.last_name_eng}
              onChange={handleChange}
              placeholder={t('signup.lastNameEngPlaceholder')}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="nickname">{t('signup.nickname')} {t('signup.required')}</label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            value={formData.nickname}
            onChange={handleChange}
            placeholder={t('signup.nicknamePlaceholder')}
            required
            minLength="3"
            maxLength="50"
          />
          {/* <small style={{ color: 'var(--c-718096-fg)', display: 'block', marginTop: '5px' }}>
            ⚠️ {t('signup.nicknameHint')}
          </small> */}
        </div>

        <div className="form-group">
          <label htmlFor="email">{t('signup.email')} {t('signup.required')}</label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t('signup.emailPlaceholder')}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">{t('signup.password')} {t('signup.required')}</label>
          <div className="password-input-wrapper">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              placeholder={t('signup.passwordPlaceholder')}
              required
              minLength="6"
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>{t('signup.avatar')}</label>
          
          {avatarPreview && (
            <div style={{ marginBottom: '15px', textAlign: 'center' }}>
              <img 
                src={avatarPreview} 
                alt={t('a11y.avatarPreview')} 
                style={{ 
                  width: '120px', 
                  height: '120px', 
                  borderRadius: '50%', 
                  objectFit: 'cover',
                  border: '3px solid #667eea'
                }} 
              />
              <div style={{ marginTop: '10px' }}>
                <button 
                  type="button" 
                  onClick={handleRemoveAvatar}
                  className="btn btn-secondary"
                  style={{ padding: '8px 20px', fontSize: '14px' }}
                >
                  {t('signup.removeAvatar')}
                </button>
              </div>
            </div>
          )}

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleAvatarFileChange}
              style={{ display: 'none' }}
              id="avatar-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn btn-secondary"
              style={{ width: '100%', marginBottom: '5px' }}
            >
              {avatarFile ? t('signup.changeAvatarFile') : t('signup.chooseAvatarFile')}
            </button>
            {avatarFile && (
              <div style={{ fontSize: '14px', color: 'var(--c-4a5568-fg)', marginTop: '5px' }}>
                {avatarFile.name}
              </div>
            )}
            <small style={{ color: 'var(--c-718096-fg)', display: 'block', marginTop: '5px' }}>
              {t('signup.avatarFileHint')}
            </small>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="gender">{t('signup.gender')}</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <option value="">{t('signup.selectGender')}</option>
              <option value="male">{t('signup.male')}</option>
              <option value="female">{t('signup.female')}</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="birth_date">{t('signup.birthDate')}</label>
            <DatePicker
              value={formData.birth_date}
              onChange={(value) => setFormData(prev => ({ ...prev, birth_date: value }))}
              placeholder={t('signup.birthDatePlaceholder')}
              language={language}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="weight">{t('signup.weight')}</label>
            <input
              id="weight"
              name="weight"
              type="number"
              step="0.1"
              value={formData.weight}
              onChange={handleChange}
              placeholder={t('signup.weightPlaceholder')}
              min="1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="ftp">{t('signup.ftp')}</label>
            <input
              id="ftp"
              name="ftp"
              type="number"
              step="1"
              value={formData.ftp}
              onChange={handleChange}
              placeholder={t('signup.ftpPlaceholder')}
              min="1"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="country">{t('signup.country')}</label>
            <CountrySelect
              value={formData.country}
              onChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
              language={language}
            />
          </div>

          <div className="form-group">
            <label htmlFor="time_zone">{t('signup.timeZone')}</label>
            <TimeZoneSelect
              value={formData.time_zone}
              onChange={(value) => setFormData(prev => ({ ...prev, time_zone: value }))}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="language">{t('signup.language')}</label>
          <select
            id="language"
            name="language"
            value={formData.language}
            onChange={handleChange}
          >
            {LANGUAGE_OPTIONS.map(({ code, name }) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? t('auth.creatingAccount') : t('auth.signUp')}
        </button>
      </form>

      <div className="auth-link">
        {t('auth.alreadyHaveAccount')} <Link to="/login">{t('auth.signIn')}</Link>
      </div>
    </div>
  );
};

export default Signup;